import { gql } from '@apollo/client';
import { css } from '@emotion/core';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { BILLING_PREFERENCES_PATH, pathToRoute } from '../constants/routes';
import useEnvConfig from '../hooks/useEnvConfig';
import { usePaperBillFeeFlag } from '../hooks/useGetFeatureFlag';
import useSearchParams from '../hooks/useSearchParams';
import EmailInvoiceButton from './EmailInvoiceButton';
import ViewBillButton from './ViewBillButton';

const MOST_RECENT_INVOICE_FRAGMENT = gql`
  fragment MostRecentInvoiceFields on Prospect {
    id
    state
    billing(contactId: $contactId, hybridAuthToken: $hybridAuthToken) {
      id
      transactions {
        id
        date
        invoiceKey
      }
    }
  }
`;

const MostRecentInvoice = ({ data }) => {
  const { prospectId, contactId } = useParams();
  const config = useEnvConfig();
  const history = useHistory();
  const searchParams = useSearchParams();
  const { enabled: isPaperBillFeeEnabled } = usePaperBillFeeFlag();
  const hideNav = searchParams.hideNav === 'true';

  const showBillingPreferences = isPaperBillFeeEnabled;

  // Mock data for specific prospect in non-production environments
  const allowedEnvs = ['majstg', 'local', 'devmaj'];
  const shouldUseMock = prospectId === '1770578041' && allowedEnvs.includes(config.paymentEnv);

  const transactionWithMostRecentInvoice = shouldUseMock
    ? {
        id: 'mock-transaction-id',
        date: DateTime.local().minus({ months: 1 }).toISO(),
        invoiceKey: 'mock-invoice-key-12345',
      }
    : data?.prospect?.billing?.transactions?.find((transaction) => transaction.invoiceKey);

  const navigateToBillPreferences = () => {
    history.push({
      pathname: pathToRoute(BILLING_PREFERENCES_PATH, { prospectId, contactId }),
      search: hideNav ? '?hideNav=true' : '',
    });
  };

  if (
    !transactionWithMostRecentInvoice ||
    !transactionWithMostRecentInvoice.date ||
    DateTime.local()
      .diff(DateTime.fromISO(transactionWithMostRecentInvoice.date, { setZone: true }), 'months')
      .toObject().months >= 12
  ) {
    return null;
  }
  return (
    <Grid item xs>
      <Card
        css={css`
          height: 100%;
          display: flex;
          flex-direction: column;
        `}
      >
        <CardHeader title="Invoice" />
        <CardContent
          css={css`
            flex-grow: 1;
          `}
        />
        <div style={{ display: 'flex', gap: '20px' }}>
          <CardActions>
            <ViewBillButton
              small={false}
              invoiceKey={transactionWithMostRecentInvoice.invoiceKey}
              invoiceDate={transactionWithMostRecentInvoice.date}
              contactId={contactId}
            />
            <EmailInvoiceButton
              small={false}
              invoiceKey={transactionWithMostRecentInvoice.invoiceKey}
              invoiceDate={transactionWithMostRecentInvoice.date}
              contactId={contactId}
              prospectId={prospectId}
              amount={transactionWithMostRecentInvoice.amount}
              comments={transactionWithMostRecentInvoice.comments}
            />
          </CardActions>
          {showBillingPreferences && (
            <CardActions>
              <Button
                variant="text"
                color="primary"
                onClick={navigateToBillPreferences}
                data-testid="bill-preferences-btn"
              >
                BILL PREFERENCES
              </Button>
            </CardActions>
          )}
        </div>
      </Card>
    </Grid>
  );
};

MostRecentInvoice.propTypes = {
  data: PropTypes.shape({
    prospect: PropTypes.shape({
      state: PropTypes.string,
      billing: PropTypes.shape({
        transactions: PropTypes.arrayOf(
          PropTypes.shape({
            date: PropTypes.string,
            invoiceKey: PropTypes.string,
          }),
        ),
      }),
    }),
  }),
  prospectId: PropTypes.string.isRequired,
};

export { MOST_RECENT_INVOICE_FRAGMENT };
export default MostRecentInvoice;
